// vitest からは Nuxt auto-import が効かないため明示 import に。
import { ref, readonly, watchEffect, type Ref } from 'vue'
import { mergeById } from '~/utils/merge-by-id'
import { storage } from '~/utils/session-storage'
import { useSubProfile } from './sub-profile'
import {
  contractSubscriptionRequestByCreditCard,
  getSubscriptionRequestByToken,
  getSubscriptionRequestDetail,
  linkSubscriptionRequest,
  searchSubscriptionRequests,
} from './api/subscription-request'
import type { SubscriptionContract } from './subscription-contract'
import type { SubscriptionEntry } from './subscription-entry'
import type {
  GetSubscriptionRequest200,
  GetSubscriptionRequestByToken200,
  GetSubscriptionRequestList200,
} from '~/types/api/SubscriptionRequest'
import type { CamelCaseKeys } from '~/types/util'

export type SubscriptionRequestBase = CamelCaseKeys<GetSubscriptionRequestList200>[number]
export type SubscriptionRequest = CamelCaseKeys<GetSubscriptionRequest200>
// 契約URLのトークン取得(token_show)の表示専用レスポンス。所有者スコープの SubscriptionRequest と
// 表示に使うフィールドは同形のため、state(SubscriptionRequest)へそのまま格納できる。
export type SubscriptionRequestByToken = CamelCaseKeys<GetSubscriptionRequestByToken200>

const subscriptionRequest = ref(storage<SubscriptionRequest>('subscriptionRequest').getItem())
watchEffect(() =>
  storage<SubscriptionRequest>('subscriptionRequest').setItem(subscriptionRequest.value)
)

const searchList = async () => {
  const targets = await useSubProfile().getTargets()
  const lists = await Promise.all(
    targets.map((target) => searchSubscriptionRequests(target.proxyGroupMemberId))
  )
  return mergeById(lists)
}

const getDetail = async (id: number, targetGroupMemberId?: number) => {
  subscriptionRequest.value = await getSubscriptionRequestDetail(id, targetGroupMemberId)
  return readonly(subscriptionRequest)
}

// 契約URLのトークンで表示専用に取得し state へ格納する。新規患者(glen未リンク)で未所有の
// 申込でも、所有者スコープ取得(getDetail)のように 404 にならず表示できる。
const getDetailByToken = async (id: number, linkToken: string) => {
  subscriptionRequest.value = (await getSubscriptionRequestByToken(
    id,
    linkToken
  )) as SubscriptionRequest
  return readonly(subscriptionRequest)
}

const contractByCreditCard = async (entry: SubscriptionEntry) => {
  return (await contractSubscriptionRequestByCreditCard(entry)) as SubscriptionContract
}

const link = async (subscriptionId: number, linkToken: string, targetGroupMemberId?: number) => {
  return await linkSubscriptionRequest(subscriptionId, linkToken, targetGroupMemberId)
}

export const useSubscriptionRequest = () => {
  return {
    subscriptionRequest: readonly(subscriptionRequest) as Readonly<Ref<SubscriptionRequest>>,
    searchList,
    getDetail,
    getDetailByToken,
    link,
    contractByCreditCard,
  }
}
